import { useEffect, useState } from 'react';
import { formatDate } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollText, Plus } from 'lucide-react';
import { SelectFilter, SearchInput, DateRangeFilter, ClearFilters, type Option } from '@/components/filters';
import { buildQuery, type DateRange } from '@/lib/filters';
import { StatTiles } from '@/components/StatTiles';
import { PageHeader } from '@/components/PageHeader';
import { Pagination, DEFAULT_PAGE_SIZE } from '@/components/Pagination';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface Policy {
  id: string;
  name: string;
  description?: string;
  productId?: string;
  productName?: string;
  licenseType: string;
  duration?: number | null;
  maxActivations: number;
  maxUsers?: number | null;
  features?: any;
  numCameras?: number | null;
  activationMode: string;
  floating: boolean;
  requireHeartbeat: boolean;
  heartbeatDuration?: number | null;
  createdAt?: number;
  created_at?: number;
}

type PolicyForm = {
  name: string;
  description: string;
  productId: string;
  licenseType: string;
  activationMode: string;
  duration: string; // seconds; empty = perpetual
  maxActivations: string;
  maxUsers: string;
  floating: boolean;
  requireHeartbeat: boolean;
  heartbeatDuration: string;
  numCameras: string;          // blank = not set
  features: string; // comma-separated
};

const EMPTY_FORM: PolicyForm = {
  name: '',
  description: '',
  productId: '',
  licenseType: 'STANDARD',
  activationMode: 'ONLINE',
  duration: '',
  maxActivations: '1',
  maxUsers: '',
  floating: false,
  requireHeartbeat: false,
  heartbeatDuration: '600',
  numCameras: '',
  features: '',
};

// Raw seconds are unreadable at a glance; show the term the license will get.
function formatDuration(seconds: number): string {
  const d = Math.round(seconds / 86400);
  if (d >= 365 && d % 365 === 0) return `${d / 365} year${d / 365 > 1 ? 's' : ''}`;
  if (d >= 30 && d % 30 === 0) return `${d / 30} month${d / 30 > 1 ? 's' : ''}`;
  return `${d} day${d === 1 ? '' : 's'}`;
}

const LICENSE_TYPES = ['TRIAL', 'STANDARD', 'PREMIUM', 'ENTERPRISE'];
const ACTIVATION_MODES = ['ONLINE', 'AIRGAP', 'FLOATING'];

// `features` is stored either as a plain array of names, or as
// {analytics:[...], numCameras:N} when a camera count is set. Both are read
// back here so editing an existing policy never loses data.
function splitFeatures(raw: any): { names: string; cameras: string } {
  if (Array.isArray(raw)) return { names: raw.join(', '), cameras: '' };
  if (raw && typeof raw === 'object') {
    const names = Array.isArray(raw.analytics)
      ? raw.analytics.join(', ')
      : Object.keys(raw).filter((k) => k !== 'numCameras' && raw[k] === true).join(', ');
    const n = raw.numCameras ?? raw.num_cameras;
    return { names, cameras: n == null ? '' : String(n) };
  }
  return { names: raw ? String(raw) : '', cameras: '' };
}

function toForm(p: Policy): PolicyForm {
  const { names: features, cameras } = splitFeatures(p.features);
  return {
    name: p.name,
    description: p.description || '',
    productId: p.productId || '',
    licenseType: p.licenseType,
    activationMode: p.activationMode,
    duration: p.duration != null ? String(p.duration) : '',
    maxActivations: String(p.maxActivations ?? 1),
    maxUsers: p.maxUsers != null ? String(p.maxUsers) : '',
    floating: !!p.floating,
    requireHeartbeat: !!p.requireHeartbeat,
    heartbeatDuration: p.heartbeatDuration != null ? String(p.heartbeatDuration) : '',
    numCameras: cameras,
    features,
  };
}

function formToPayload(f: PolicyForm) {
  const num = (s: string) => (s.trim() === '' ? null : Number(s));
  return {
    name: f.name,
    description: f.description || undefined,
    // Required: the server rejects a policy with no product (it would be an
    // unusable template) and stamps the organization from the session.
    productId: f.productId,
    licenseType: f.licenseType,
    activationMode: f.activationMode,
    duration: num(f.duration),
    maxActivations: Number(f.maxActivations || '1'),
    maxUsers: num(f.maxUsers),
    floating: f.floating,
    requireHeartbeat: f.requireHeartbeat,
    heartbeatDuration: num(f.heartbeatDuration),
    // With a camera count we must use the object shape the SDK normalizes
    // ({analytics:[...], numCameras:N}); without one, keep the plain array so
    // existing policies and consumers are unaffected.
    features: (() => {
      const names = f.features.trim()
        ? f.features.split(',').map((s) => s.trim()).filter(Boolean)
        : [];
      const cams = f.numCameras.trim() === '' ? null : Number(f.numCameras);
      if (cams != null && !Number.isNaN(cams)) return { analytics: names, numCameras: cams };
      return names.length ? names : null;
    })(),
  };
}

function Policies() {
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [form, setForm] = useState<PolicyForm>(EMPTY_FORM);

  const apiUrl = import.meta.env.VITE_API_URL || '';
  const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  });

  const [products, setProducts] = useState<Array<{ id: string; name: string }>>([]);
  const [search, setSearch] = useState('');
  const [productFilter, setProductFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateRange, setDateRange] = useState<DateRange>({});

  useEffect(() => {
    const id = setTimeout(fetchPolicies, search ? 300 : 0);
    return () => clearTimeout(id);
  }, [search, productFilter, typeFilter, dateRange.from, dateRange.to, page]);

  // A filter change invalidates the current page — otherwise you can sit on
  // page 5 of a result set that now has one page and see an empty table.
  useEffect(() => { setPage(1); }, [search, productFilter, typeFilter, dateRange.from, dateRange.to]);

  useEffect(() => {
    (async () => {
      const res = await fetch(`${apiUrl}/api/products?limit=1000`, { headers: authHeaders() });
      if (res.ok) {
        const j = await res.json();
        setProducts((j.data || []).map((p: any) => ({ id: p.id, name: p.name })));
      }
    })().catch(() => {});
  }, []);

  const clearFilters = () => {
    setSearch('');
    setProductFilter('all');
    setTypeFilter('all');
    setDateRange({});
  };

  const fetchPolicies = async () => {
    try {
      const qs = buildQuery({
        limit: DEFAULT_PAGE_SIZE,
        page,
        search,
        productId: productFilter,
        licenseType: typeFilter,
        from: dateRange.from,
        to: dateRange.to,
      });
      const res = await fetch(`${apiUrl}/api/policies${qs}`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setPolicies(data.data?.policies || []);
        setTotal(data.data?.total ?? 0);
      }
    } catch (error) {
      console.error('Failed to fetch policies:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const res = await fetch(`${apiUrl}/api/policies`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(formToPayload(form)),
      });
      if (res.ok) {
        setFormError(null);
        setIsCreateOpen(false);
        setForm(EMPTY_FORM);
        fetchPolicies();
      } else {
        const body = await res.json().catch(() => ({}));
        setFormError(body?.error || 'Failed to create policy');
      }
    } catch (error) {
      console.error('Failed to create policy:', error);
      setFormError('Failed to create policy');
    }
  };

  const handleEdit = async () => {
    if (!editId) return;
    try {
      const res = await fetch(`${apiUrl}/api/policies/${editId}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(formToPayload(form)),
      });
      if (res.ok) {
        setFormError(null);
        setIsEditOpen(false);
        setEditId(null);
        fetchPolicies();
      } else {
        const body = await res.json().catch(() => ({}));
        setFormError(body?.error || 'Failed to update policy');
      }
    } catch (error) {
      console.error('Failed to update policy:', error);
      setFormError('Failed to update policy');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this policy? Licenses referencing it will block deletion.')) return;
    try {
      const res = await fetch(`${apiUrl}/api/policies/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (res.ok) {
        fetchPolicies();
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.error || 'Failed to delete policy');
      }
    } catch (error) {
      console.error('Failed to delete policy:', error);
    }
  };

  const openEdit = (p: Policy) => {
    setFormError(null);
    setEditId(p.id);
    setForm(toForm(p));
    setIsEditOpen(true);
  };

  const fields = (mode: 'create' | 'edit') => (
    <div className="grid gap-4 py-4">
      <div className="grid gap-2">
        <Label>Name</Label>
        <Input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Pro Annual"
        />
      </div>
      <div className="grid gap-2">
        <Label>Description (optional)</Label>
        <Input
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
      </div>
      <div className="grid gap-2">
        <Label>
          Product <span className="text-destructive">*</span>
        </Label>
        <Select
          value={form.productId}
          onValueChange={(v) => setForm({ ...form, productId: v })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a product" />
          </SelectTrigger>
          <SelectContent>
            {products.map((p) => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          A policy is a template for one product. Only products in your organization are listed;
          the organization is set automatically from your session.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="grid gap-2">
          <Label>License Type</Label>
          <Select value={form.licenseType} onValueChange={(v) => setForm({ ...form, licenseType: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {LICENSE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-2">
          <Label>Activation Mode</Label>
          <Select value={form.activationMode} onValueChange={(v) => setForm({ ...form, activationMode: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {ACTIVATION_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="grid gap-2">
          <Label>Duration (seconds, blank = perpetual)</Label>
          <Input
            type="number"
            value={form.duration}
            onChange={(e) => setForm({ ...form, duration: e.target.value })}
            placeholder="31536000"
          />
        </div>
        <div className="grid gap-2">
          <Label>Max Activations (seats)</Label>
          <Input
            type="number"
            value={form.maxActivations}
            onChange={(e) => setForm({ ...form, maxActivations: e.target.value })}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="grid gap-2">
          <Label>Max Users (optional)</Label>
          <Input
            type="number"
            value={form.maxUsers}
            onChange={(e) => setForm({ ...form, maxUsers: e.target.value })}
          />
        </div>
        <div className="grid gap-2">
          <Label>Heartbeat Duration (seconds)</Label>
          <Input
            type="number"
            value={form.heartbeatDuration}
            onChange={(e) => setForm({ ...form, heartbeatDuration: e.target.value })}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="grid gap-2">
          <Label>Floating</Label>
          <Select
            value={form.floating ? 'yes' : 'no'}
            onValueChange={(v) => setForm({ ...form, floating: v === 'yes' })}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="no">No</SelectItem>
              <SelectItem value="yes">Yes</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-2">
          <Label>Require Heartbeat</Label>
          <Select
            value={form.requireHeartbeat ? 'yes' : 'no'}
            onValueChange={(v) => setForm({ ...form, requireHeartbeat: v === 'yes' })}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="no">No</SelectItem>
              <SelectItem value="yes">Yes</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid gap-2">
        <Label>Features (comma-separated)</Label>
        <Input
          value={form.features}
          onChange={(e) => setForm({ ...form, features: e.target.value })}
          placeholder="live_view, recording, anpr, people_count"
        />
        <p className="text-xs text-muted-foreground">
          These exact strings are what the device checks — <code>sdk_verify_feature("anpr")</code>.
          They must match your application code character-for-character.
        </p>
      </div>
      <div className="grid gap-2">
        <Label>Number of Cameras (blank = not set)</Label>
        <Input
          type="number"
          min={0}
          value={form.numCameras}
          onChange={(e) => setForm({ ...form, numCameras: e.target.value })}
          placeholder="16"
        />
        <p className="text-xs text-muted-foreground">
          Read on the device via <code>sdk_get_num_cameras()</code>. Every license created from
          this policy inherits these entitlements and its expiry from the duration above.
        </p>
      </div>
      {formError && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {formError}
        </p>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={() => (mode === 'create' ? setIsCreateOpen(false) : setIsEditOpen(false))}>
          Cancel
        </Button>
        {/* Product is mandatory — the server rejects a policy without one. */}
        <Button
          onClick={mode === 'create' ? handleCreate : handleEdit}
          disabled={!form.name || !form.productId}
        >
          {mode === 'create' ? 'Create Policy' : 'Update Policy'}
        </Button>
      </DialogFooter>
    </div>
  );

  if (isLoading) {
    return (
      <div className="w-full px-4 py-6 sm:px-6 lg:px-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">Loading...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        eyebrow="License management"
        icon={ScrollText}
        title="Policies"
        subtitle="Reusable license templates. Licenses created with a policy inherit its defaults."
        actions={(
          <Button onClick={() => { setForm(EMPTY_FORM); setFormError(null); setIsCreateOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Add Policy
          </Button>
        )}
      />

      <StatTiles stats={[
        { label: 'Total', value: policies.length },
        { label: 'Standard', value: policies.filter((p) => p.licenseType === 'STANDARD').length },
        { label: 'Premium', value: policies.filter((p) => p.licenseType === 'PREMIUM').length },
        { label: 'Enterprise', value: policies.filter((p) => p.licenseType === 'ENTERPRISE').length },
        { label: 'Trial', value: policies.filter((p) => p.licenseType === 'TRIAL').length },
        { label: 'Floating', value: policies.filter((p) => p.floating === true).length },
      ]} />

      <Card>
        <CardHeader>
          <CardTitle>All Policies</CardTitle>
          <CardDescription>{policies.length} policies</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="filter-bar">
            <SearchInput value={search} onChange={setSearch} placeholder="Name or description…" />
            <SelectFilter
              label="Product"
              value={productFilter}
              onChange={setProductFilter}
              width="w-56"
              options={products.map((p): Option => ({ value: p.id, label: p.name }))}
            />
            <SelectFilter
              label="License type"
              value={typeFilter}
              onChange={setTypeFilter}
              options={['TRIAL', 'STANDARD', 'PREMIUM', 'ENTERPRISE'].map((v) => ({ value: v, label: v }))}
            />
            <DateRangeFilter label="Created" value={dateRange} onChange={setDateRange} />
            <ClearFilters onClick={clearFilters} />
          </div>
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Mode</TableHead>
                <TableHead>Seats</TableHead>
                <TableHead>Cameras</TableHead>
                <TableHead>Features</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Floating</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {policies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} className="empty-state">
                    <p>No policies yet</p>
                  </TableCell>
                </TableRow>
              ) : (
                policies.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell>{p.productName || <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="border-primary/20 bg-primary/10 text-primary">
                        {p.licenseType}
                      </Badge>
                    </TableCell>
                    <TableCell>{p.activationMode}</TableCell>
                    <TableCell>{p.maxActivations}</TableCell>
                    <TableCell>
                      {splitFeatures(p.features).cameras || (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[22rem]">
                      {(() => {
                        const names = splitFeatures(p.features).names;
                        if (!names) return <span className="text-muted-foreground">—</span>;
                        const list = names.split(',').map((x) => x.trim()).filter(Boolean);
                        return (
                          <span className="text-xs" title={list.join(', ')}>
                            {list.slice(0, 3).join(', ')}
                            {list.length > 3 && ` +${list.length - 3} more`}
                          </span>
                        );
                      })()}
                    </TableCell>
                    <TableCell>{p.duration ? formatDuration(p.duration) : 'Perpetual'}</TableCell>
                    <TableCell>
                      {p.floating ? (
                        <Badge variant="outline" className="border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">
                          Yes
                        </Badge>
                      ) : 'No'}
                    </TableCell>
                    <TableCell>{formatDate(p.created_at || p.createdAt)}</TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(p)}>Edit</Button>
                      <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(p.id)}>Delete</Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <Pagination page={page} pageSize={DEFAULT_PAGE_SIZE} total={total} onPageChange={setPage} label="policies" />
        </CardContent>
      </Card>

      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Policy</DialogTitle>
            <DialogDescription>Define a reusable license template.</DialogDescription>
          </DialogHeader>
          {fields('create')}
        </DialogContent>
      </Dialog>

      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Policy</DialogTitle>
            <DialogDescription>Update this license template.</DialogDescription>
          </DialogHeader>
          {fields('edit')}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Policies;
